import { useState } from 'react';
import { CuadroDeBusqueda } from '../Buscable';
import { filtrarFilas } from '../busqueda';
import { useQuery } from '@tanstack/react-query';

import { almacen, type Supply } from '../api';
import { filtroDeLista, type FiltroDeLista } from '../estadoDelStock';
import {
  BarraDeNivel,
  Cabecera,
  Cargando,
  ConAyuda,
  Fallo,
  Filtro,
  Pastilla,
  PuntoDeNivel,
  Vacio,
} from '../componentes';
import { NIVEL, VENCIMIENTO, cantidad, dia, soles } from '../formato';
import { Empaques } from '../Empaques';
import { FichaDeInsumo, Retirados, Retirar } from '../formularios';
import { mayuscula, useVocabulario } from '../vocabulario';
import { PorPrenda } from './PorPrenda';

type FiltroNivel = 'todos' | FiltroDeLista;

// Agotados primero, que es lo que más urge. Faltaba: un almacén con todo en
// cero enseñaba «Críticos 0 · Bajo mínimo 0» y parecía que nada iba mal.
const NIVELES: { id: FiltroNivel; texto: string }[] = [
  { id: 'todos', texto: 'Todos' },
  { id: 'out', texto: 'Agotados' },
  { id: 'critical', texto: 'Críticos' },
  { id: 'low', texto: 'Bajo mínimo' },
  { id: 'ok', texto: 'En rango' },
  { id: 'over', texto: 'Sobrestock' },
  // Lo que nunca se registró: aparte de «Agotados», que es lo que se acabó.
  { id: 'sin-contar', texto: 'Sin contar' },
];

/**
 * El catálogo con el stock de este local.
 *
 * La tabla se lee de izquierda a derecha en el orden en que se pregunta: qué
 * es, de qué categoría, cuánto queda, cuánto debería quedar, cuánto vale y
 * dónde está guardado.
 */
export function Insumos() {
  const [nivel, setNivel] = useState<FiltroNivel>('todos');
  const [categoria, setCategoria] = useState('todas');
  const [texto, setTexto] = useState('');
  const vocabulario = useVocabulario();

  const consulta = useQuery({ queryKey: ['insumos'], queryFn: almacen.supplies });

  /*
   * Las prendas con tallas, si el local las tiene. Sin ninguna —un
   * restaurante, una bodega— la pantalla es la de siempre y ni se ve el
   * botón: lo decide lo que hay, no el rubro.
   */
  const prendas = useQuery({ queryKey: ['prendas'], queryFn: almacen.variantFamilies });
  const familias = prendas.data ?? [];
  const [porPrenda, setPorPrenda] = useState(false);
  const enMatriz = porPrenda && familias.length > 0;

  const insumos = consulta.data ?? [];
  const categorias = [...new Set(insumos.map((insumo) => insumo.category))];

  const visibles = filtrarFilas(
    insumos.filter(
      (insumo) =>
        (nivel === 'todos' || filtroDeLista(insumo) === nivel) &&
        (categoria === 'todas' || insumo.category === categoria),
    ),
    texto,
    (insumo) => [
      insumo.name,
      insumo.sku,
      insumo.category,
      insumo.supplier,
      insumo.location,
      insumo.barcode,
    ],
  );

  const cuantos = (id: FiltroNivel) =>
    id === 'todos'
      ? insumos.length
      : insumos.filter((insumo) => filtroDeLista(insumo) === id).length;

  return (
    <>
      <Cabecera
        titulo={mayuscula(vocabulario.insumos)}
        subtitulo="Catálogo con stock, mínimos y costo promedio" ayuda="Todo lo que el local guarda, con cuánto hay de cada cosa, su mínimo y lo que vale. Desde aquí se da de alta, se edita y se retira."
        acciones={
          <>
            {familias.length === 0 ? null : (
              <Filtro
                texto={enMatriz ? 'Ver la lista' : vocabulario.porFamilia}
                {...(enMatriz ? {} : { cuenta: familias.length })}
                activo={enMatriz}
                onClick={() => setPorPrenda(!porPrenda)}
              />
            )}
            <Retirados que="insumos" />
            <FichaDeInsumo />
          </>
        }
      />

      <div className="cuerpo">
        {consulta.isError ? (
          <Fallo error={consulta.error} onReintentar={() => consulta.refetch()} />
        ) : null}

        <div className="filtros">
          <CuadroDeBusqueda
            valor={texto}
            onCambio={setTexto}
            placeholder={`Buscar ${vocabulario.insumo}, código, proveedor…`}
            etiquetaAccesible={`Buscar ${vocabulario.insumo}`}
          />

          <span className="separador" />

          {NIVELES.map((opcion) => (
            <Filtro
              key={opcion.id}
              texto={opcion.texto}
              cuenta={cuantos(opcion.id)}
              activo={nivel === opcion.id}
              onClick={() => setNivel(opcion.id)}
            />
          ))}

          <span className="separador" />

          <Filtro
            texto="Toda categoría"
            activo={categoria === 'todas'}
            onClick={() => setCategoria('todas')}
          />
          {categorias.map((nombre) => (
            <Filtro
              key={nombre}
              texto={nombre}
              activo={categoria === nombre}
              onClick={() => setCategoria(nombre)}
            />
          ))}

          <span className="recuento">
            {visibles.length} DE {insumos.length} {vocabulario.insumos.toUpperCase()}
          </span>
        </div>

        {enMatriz ? (
          <PorPrenda familias={familias} insumos={insumos} />
        ) : consulta.isLoading ? (
          <Cargando que="el catálogo" />
        ) : visibles.length === 0 ? (
          <Vacio
            titulo={
              insumos.length === 0 ? 'El catálogo está vacío' : 'Nada con ese filtro'
            }
            mensaje={
              insumos.length === 0
                ? 'Da de alta lo que el local guarda: qué es, en qué se cuenta y cuánto se quiere tener. Sin catálogo no hay nada que recibir ni que contar.'
                : `Ningún ${vocabulario.insumo} está en esa situación ahora mismo. Prueba con otro filtro o mira el catálogo entero.`
            }
            accion={insumos.length === 0 ? <FichaDeInsumo /> : undefined}
          />
        ) : (
          <Tabla insumos={visibles} />
        )}
      </div>
    </>
  );
}

function Tabla({ insumos }: { insumos: Supply[] }) {
  const vocabulario = useVocabulario();

  return (
    <div className="tabla-envoltura">
      <table>
        <thead>
          <tr>
            <th>{mayuscula(vocabulario.insumo)}</th>
            <th>Categoría</th>
            <th><ConAyuda ayuda="Cuánto hay y cómo está respecto al mínimo y al máximo.">Nivel de stock</ConAyuda></th>
            <th className="numero"><ConAyuda ayuda="El mínimo avisa que hay que comprar; el máximo dice hasta dónde llenar.">Mín / Máx</ConAyuda></th>
            <th className="numero"><ConAyuda ayuda="El costo promedio de lo que hay en el estante, sin IGV. Se recalcula con cada compra.">Costo unit.</ConAyuda></th>
            <th className="numero"><ConAyuda ayuda="Cuánto vale lo que hay: cantidad por costo promedio.">Valorizado</ConAyuda></th>
            <th>Ubicación</th>
            <th aria-label="Acciones" />
          </tr>
        </thead>
        <tbody>
          {insumos.map((insumo) => (
            <Fila key={insumo.id} insumo={insumo} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Una fila del catálogo, que se abre para enseñar sus lotes.
 *
 * «Tengo doce kilos de pollo» no dice lo que hace falta saber: si son de tres
 * entradas distintas y una vence mañana. Eso estaba en la pantalla de Lotes,
 * que los lista todos juntos y obliga a buscar el insumo entre los demás.
 *
 * Los lotes **se piden al abrir**, no con la tabla: un almacén de doscientos
 * insumos no puede traerse todos sus lotes para enseñar una lista.
 */
function Fila({ insumo }: { insumo: Supply }) {
  const [abierto, setAbierto] = useState(false);
  const vocabulario = useVocabulario();
  const estado = NIVEL[insumo.level];

  const lotes = useQuery({
    queryKey: ['lotes', insumo.id],
    queryFn: () => almacen.lots(insumo.id),
    enabled: abierto,
  });

  return (
    <>
              <tr>
                <td>
                  <div className="celda-nombre">
                    <PuntoDeNivel tono={estado.tono} />
                    {/*
                     * La miniatura solo si la hay. Un hueco gris por cada
                     * insumo sin foto llenaría la tabla de vacíos y haría
                     * pensar que algo falló al cargar.
                     */}
                    {insumo.imageUrl === null ? null : (
                      <img
                        src={insumo.imageUrl}
                        alt=""
                        className="celda-nombre__foto"
                        loading="lazy"
                      />
                    )}
                    <div className="celda-nombre__texto">
                      {/*
                       * El nombre abre sus lotes. Toda la celda y no un icono
                       * al lado: es lo que se toca cuando uno se pregunta «de
                       * dónde salieron estos doce kilos».
                       */}
                      <button
                        type="button"
                        className="celda-nombre__abrir"
                        aria-expanded={abierto}
                        onClick={() => setAbierto((estaba) => !estaba)}
                      >
                        <span aria-hidden="true">{abierto ? '–' : '+'}</span>
                        <strong>{insumo.name}</strong>
                      </button>
                      <span>{insumo.sku}</span>
                    </div>
                  </div>
                </td>
                <td className="apagado">{insumo.category}</td>
                <td>
                  <div className="celda-nivel">
                    <span className="mono">
                      {cantidad(insumo.quantity)} {insumo.unit}
                    </span>
                    <Pastilla texto={estado.texto} tono={estado.tono} />
                  </div>
                  {/*
                   * Lo que una cotización separó sigue en el estante, pero ya
                   * tiene dueño: sin decirlo, el almacén y la caja vendían dos
                   * veces el mismo rollo (8 oct).
                   */}
                  {insumo.committed !== undefined && /[1-9]/.test(insumo.committed) ? (
                    <span className="apagado">
                      {cantidad(insumo.committed)} {insumo.unit} separado por cotizaciones
                    </span>
                  ) : null}
                  <BarraDeNivel
                    cantidad={insumo.quantity}
                    maximo={insumo.maximum}
                    tono={estado.tono}
                  />
                </td>
                <td className="numero apagado">
                  {cantidad(insumo.minimum)} / {cantidad(insumo.maximum)}
                </td>
                <td className="numero">{soles(insumo.averageCost)}</td>
                <td className="numero">{soles(insumo.value)}</td>
                <td className="apagado">{insumo.location ?? '—'}</td>
                <td className="numero">
                  {/*
                   * «¿Desde cuándo no cuadra el pulpo?» se responde en el
                   * kardex, y hasta ahora había que ir allí y bajar por el
                   * libro entero. Desde la fila que se está mirando.
                   */}
                  <a className="accion-fila" href={`#/kardex/${insumo.id}`}>
                    Historial
                  </a>
                  {/*
                   * Editar aquí y no en una pantalla aparte: se llega para
                   * corregir un código mal tecleado o un mínimo que cambió, y
                   * el insumo que se quiere tocar es el que se está mirando.
                   */}
                  <FichaDeInsumo insumo={insumo} />
                  {/*
                   * En qué llega: el rollo de 50 m y el de 100 m. Lo que va
                   * por serie se recibe equipo por equipo y no tiene empaques.
                   */}
                  {insumo.isSerialTracked ? null : <Empaques insumo={insumo} />}
                  {/*
                   * Solo con el estante en cero: retirar algo que existe lo
                   * escondería. El servidor lo mira en todos los locales.
                   */}
                  {Number.parseFloat(insumo.quantity) === 0 ? (
                    <Retirar nombre={insumo.name} retirar={() => almacen.retireSupply(insumo.id)} />
                  ) : null}
                </td>
      </tr>

      {abierto ? (
        <tr className="fila-lotes">
          <td colSpan={8}>
            {lotes.isPending ? (
              <p className="apagado">Buscando los lotes…</p>
            ) : lotes.isError ? (
              <p className="apagado">No se pudieron leer los lotes.</p>
            ) : lotes.data.length === 0 ? (
              /*
               * Sin lotes no es lo mismo que sin stock: un insumo que no se
               * sigue por lote tiene su cantidad y ningún lote que enseñar, y
               * decir «no hay lotes» a secas parecería que falta algo.
               */
              <p className="apagado">
                {insumo.isLotTracked
                  ? 'Sin lotes en el estante.'
                  : `Este ${vocabulario.insumo} no se sigue por lote.`}
              </p>
            ) : (
              <table className="tabla-anidada">
                <thead>
                  <tr>
                    <th>Lote</th>
                    <th>Proveedor</th>
                    <th className="numero">Cantidad</th>
                    <th className="numero">Costo unit.</th>
                    <th className="numero">Valorizado</th>
                    <th>Recibido</th>
                    <th>Vence</th>
                  </tr>
                </thead>
                <tbody>
                  {lotes.data.map((lote) => (
                    <tr key={lote.id}>
                      <td className="mono">{lote.code}</td>
                      <td className="apagado">{lote.supplier ?? '—'}</td>
                      <td className="numero mono">
                        {cantidad(lote.quantity)} {lote.unit}
                      </td>
                      <td className="numero">{soles(lote.unitCost)}</td>
                      <td className="numero">{soles(lote.value)}</td>
                      <td className="apagado">{dia(lote.receivedAt)}</td>
                      <td>
                        {/*
                         * Lo que de verdad se viene a mirar: cuál se va
                         * primero. El rotulo ya viene hecho del servidor
                         * —«vence en 2 días»— para que el día de Lima lo
                         * decida uno solo y no cada pantalla por su cuenta.
                         */}
                        <Pastilla
                          texto={lote.expiryLabel}
                          tono={VENCIMIENTO[lote.expiry].tono}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      ) : null}
    </>
  );
}
